import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import type { AllowedEmail, UserRole } from '../../types/crm';
import {
  assignableAllowlistRoleOptions,
  assertAllowlistUpdateAllowed,
  normalizeUserRole,
} from '../../lib/settingsPermissions';

interface AllowedEmailEditModalProps {
  open: boolean;
  row: AllowedEmail | null;
  actorRole: UserRole;
  saving: boolean;
  onClose: () => void;
  onSave: (patch: { display_name: string | null; role: UserRole }) => void;
}

export function AllowedEmailEditModal({
  open,
  row,
  actorRole,
  saving,
  onClose,
  onSave,
}: AllowedEmailEditModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('user');

  const roleOptions = useMemo(() => assignableAllowlistRoleOptions(actorRole), [actorRole]);

  useEffect(() => {
    if (!open || !row) return;
    setDisplayName(row.display_name?.trim() ?? '');
    setRole(normalizeUserRole(row.role));
  }, [open, row]);

  useEffect(() => {
    if (!roleOptions.some((option) => option.value === role)) {
      setRole(roleOptions[0]?.value ?? 'user');
    }
  }, [role, roleOptions]);

  if (!row) return null;

  const targetRow = row;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextRole = normalizeUserRole(role);
    assertAllowlistUpdateAllowed(actorRole, normalizeUserRole(targetRow.role), {
      display_name: displayName.trim() || null,
      role: nextRole,
    });
    onSave({
      display_name: displayName.trim() || null,
      role: nextRole,
    });
  }

  return (
    <Modal title="Edit team member" open={open} onClose={onClose}>
      <form className="settings-edit-form" onSubmit={handleSubmit}>
        <dl className="settings-edit-form__meta">
          <div>
            <dt>Email</dt>
            <dd>{targetRow.email}</dd>
          </div>
          <div>
            <dt>Username</dt>
            <dd>{targetRow.username ?? '—'}</dd>
          </div>
          <div>
            <dt>ID</dt>
            <dd>
              <code className="settings-edit-form__id">{targetRow.id}</code>
            </dd>
          </div>
        </dl>

        <label className="field">
          <span className="field-label">Display name</span>
          <input
            className="field-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name"
          />
        </label>

        <label className="field">
          <span className="field-label">Role</span>
          <select
            className="field-input"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className="crm-form__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
